// Default questionnaire template based on current hardcoded structure
export const defaultQuestionnaireTemplate = {
  name: 'Website Development Questionnaire',
  title: 'Website Development Questionnaire',
  description: 'Please fill out this questionnaire to help us understand your project needs and create an accurate proposal.',
  sections: [
    {
      id: 'contact-information',
      title: 'Contact Information',
      description: 'Let us know how to reach you',
      order: 1,
      questions: [],
      subsections: [
        {
          id: 'basic-contact',
          title: 'Basic Contact',
          description: 'Your basic contact details',
          order: 1,
          questions: [
            {
              id: 'name',
              type: 'text' as const,
              title: 'Name',
              placeholder: 'Your full name',
              required: true
            },
            {
              id: 'email',
              type: 'email' as const,
              title: 'Email',
              placeholder: 'your.email@company.com',
              required: true
            },
            {
              id: 'phone',
              type: 'text' as const,
              title: 'Phone',
              placeholder: 'Your phone number',
              required: false
            }
          ]
        },
        {
          id: 'company-details',
          title: 'Company Details',
          description: 'Your company information',
          order: 2,
          questions: [
            {
              id: 'company',
              type: 'text' as const,
              title: 'Company',
              placeholder: 'Your company name',
              required: true
            },
            {
              id: 'website',
              type: 'url' as const,
              title: 'Current Website (if any)',
              placeholder: 'https://yourwebsite.com',
              required: false
            }
          ]
        }
      ]
    },
    {
      id: 'project-information',
      title: 'Project Information',
      description: 'Tell us about your project',
      order: 2,
      questions: [],
      subsections: [
        {
          id: 'project-categories',
          title: 'Project Categories',
          description: 'What type of project are you interested in?',
          order: 1,
          questions: [
            {
              id: 'projectCategories',
              type: 'checkbox' as const,
              title: 'Project Categories',
              description: 'Select all that apply to your project',
              required: true,
              options: [
                { id: 'ecommerce', label: 'E-commerce', value: 'ecommerce' },
                { id: 'restaurant', label: 'Restaurant', value: 'restaurant' },
                { id: 'realEstate', label: 'Real Estate', value: 'realEstate' },
                { id: 'hospitality', label: 'Hotels/Hospitality', value: 'hospitality' },
                { id: 'portfolio', label: 'Portfolio', value: 'portfolio' },
                { id: 'corporate', label: 'Corporate', value: 'corporate' },
                { id: 'nonprofit', label: 'Non-profit', value: 'nonprofit' }
              ]
            }
          ]
        },
        {
          id: 'blog-events',
          title: 'Blog & Events',
          description: 'Blog and events requirements',
          order: 2,
          questions: [
            {
              id: 'needsBlog',
              type: 'radio' as const,
              title: 'Do you need a blog?',
              required: false,
              options: [
                { id: 'yes', label: 'Yes', value: 'yes' },
                { id: 'no', label: 'No', value: 'no' },
                { id: 'maybe', label: 'Maybe/Unsure', value: 'maybe' }
              ]
            },
            {
              id: 'needsEvents',
              type: 'radio' as const,
              title: 'Do you need events functionality?',
              required: false,
              options: [
                { id: 'yes', label: 'Yes', value: 'yes' },
                { id: 'no', label: 'No', value: 'no' },
                { id: 'maybe', label: 'Maybe/Unsure', value: 'maybe' }
              ]
            }
          ]
        },
        {
          id: 'ada-compliance',
          title: 'ADA Compliance',
          description: 'ADA compliance requirements',
          order: 3,
          questions: [
            {
              id: 'advancedADACompliance',
              type: 'radio' as const,
              title: 'Do you require advanced ADA compliance?',
              required: false,
              options: [
                { id: 'yes', label: 'Yes', value: 'yes' },
                { id: 'no', label: 'No', value: 'no' }
              ]
            }
          ]
        },
        {
          id: 'project-description',
          title: 'Project Description',
          description: 'Details about your project',
          order: 4,
          questions: [
            {
              id: 'projectDescription',
              type: 'textarea' as const,
              title: 'Project Description',
              description: 'Describe your project in detail',
              placeholder: 'Tell us about your project goals, requirements, and vision...',
              required: true
            }
          ]
        },
        {
          id: 'target-audience',
          title: 'Target Audience',
          description: 'Who the project is for',
          order: 5,
          questions: [
            {
              id: 'targetAudience',
              type: 'textarea' as const,
              title: 'Target Audience',
              description: 'Who is your target audience?',
              placeholder: 'Demographics, interests, behaviors...',
              required: true
            }
          ]
        }
      ]
    },
    {
      id: 'timeline-budget',
      title: 'Timeline & Budget',
      description: 'Help us understand your timeline and budget',
      order: 3,
      questions: [],
      subsections: [
        {
          id: 'timeline',
          title: 'Timeline',
          description: 'Your project timeline',
          order: 1,
          questions: [
            {
              id: 'timeline',
              type: 'select' as const,
              title: 'Desired Timeline',
              required: true,
              options: [
                { id: 'asap', label: 'ASAP', value: 'asap' },
                { id: '1-2months', label: '1-2 months', value: '1-2months' },
                { id: '3-4months', label: '3-4 months', value: '3-4months' },
                { id: '6months', label: '6+ months', value: '6months' },
                { id: 'flexible', label: 'Flexible', value: 'flexible' }
              ]
            },
            {
              id: 'launchDate',
              type: 'date' as const,
              title: 'Desired Launch Date',
              required: false
            }
          ]
        },
        {
          id: 'budget',
          title: 'Budget',
          description: 'Your budget expectations',
          order: 2,
          questions: [
            {
              id: 'budget',
              type: 'range' as const,
              title: 'Budget Range',
              required: true,
              min: 0,
              max: 150000
            }
          ]
        }
      ]
    },
    {
      id: 'page-structure-sitemap',
      title: 'Page Structure & Sitemap',
      description: 'Help us understand your website structure',
      order: 4,
      questions: [],
      subsections: [
        {
          id: 'site-structure',
          title: 'Site Structure',
          description: 'Your existing sitemap',
          order: 1,
          questions: [
            {
              id: 'existingSitemap',
              type: 'radio' as const,
              title: 'Do you have an existing sitemap?',
              required: true,
              options: [
                { id: 'yes', label: 'Yes', value: 'yes' },
                { id: 'no', label: 'No', value: 'no' },
                { id: 'inProgress', label: 'In progress', value: 'inProgress' }
              ]
            }
          ]
        },
        {
          id: 'page-requirements',
          title: 'Page Requirements',
          description: 'Key pages and sections',
          order: 2,
          questions: [
            {
              id: 'brandDescription',
              type: 'textarea' as const,
              title: 'Brand Description',
              description: 'Describe your brand, style preferences, and overall vision',
              placeholder: 'Tell us about your brand personality, style preferences...',
              required: false
            }
          ]
        },
        {
          id: 'inspiration',
          title: 'Inspiration',
          description: 'Competitor and inspirational sites',
          order: 3,
          questions: [
            {
              id: 'competitorWebsites',
              type: 'textarea' as const,
              title: 'Competitor Websites',
              description: 'List competitor websites for reference',
              placeholder: 'List competitor websites and what you like/dislike about them...',
              required: false
            },
            {
              id: 'inspirationalWebsites',
              type: 'textarea' as const,
              title: 'Inspirational Websites',
              description: 'List websites you find inspiring',
              placeholder: 'List websites you find inspiring and explain why...',
              required: false
            }
          ]
        }
      ]
    },
    {
      id: 'ecommerce-features',
      title: 'E-commerce Features',
      description: 'E-commerce specific requirements',
      order: 5,
      conditionalRules: [{
        id: 'show-ecommerce-section',
        type: 'show' as const,
        conditions: [{
          questionId: 'projectCategories',
          operator: 'contains' as const,
          value: 'ecommerce'
        }]
      }],
      questions: [],
      subsections: [
        {
          id: 'ecommerce-features-main',
          title: 'E-commerce Features',
          description: 'E-commerce specific requirements',
          order: 1,
          questions: [
            {
              id: 'ecommerceProductCount',
              type: 'select' as const,
              title: 'How many products will you have?',
              required: true,
              options: [
                { id: 'less-than-5', label: 'Less than 5', value: 'less-than-5' },
                { id: 'less-than-20', label: 'Less than 20', value: 'less-than-20' },
                { id: 'less-than-50', label: 'Less than 50', value: 'less-than-50' },
                { id: 'more-than-50', label: 'More than 50', value: 'more-than-50' }
              ]
            },
            {
              id: 'ecommerceDiscounts',
              type: 'select' as const,
              title: 'Discount/Subscription Features',
              required: false,
              options: [
                { id: 'subscriptions', label: 'Subscriptions', value: 'subscriptions' },
                { id: 'bundling', label: 'Bundling Discounts', value: 'bundling' },
                { id: 'both', label: 'Both', value: 'both' },
                { id: 'none', label: 'None', value: 'none' }
              ]
            },
            {
              id: 'ecommerceComments',
              type: 'textarea' as const,
              title: 'Additional E-commerce Comments',
              description: 'Any other e-commerce requirements or notes',
              placeholder: 'Additional requirements, integrations, etc...',
              required: false
            }
          ]
        }
      ]
    },
    {
      id: 'restaurant-features',
      title: 'Restaurant Features',
      description: 'Restaurant-specific requirements',
      order: 6,
      conditionalRules: [{
        id: 'show-restaurant-section',
        type: 'show' as const,
        conditions: [{
          questionId: 'projectCategories',
          operator: 'contains' as const,
          value: 'restaurant'
        }]
      }],
      questions: [],
      subsections: [
        {
          id: 'restaurant-features-main',
          title: 'Restaurant Features',
          description: 'Restaurant-specific requirements',
          order: 1,
          questions: [
            {
              id: 'restaurantReservations',
              type: 'radio' as const,
              title: 'Do you need reservation functionality?',
              required: true,
              options: [
                { id: 'yes', label: 'Yes', value: 'yes' },
                { id: 'no', label: 'No', value: 'no' }
              ]
            },
            {
              id: 'restaurantMenuPresentation',
              type: 'select' as const,
              title: 'Menu Presentation Preference',
              required: false,
              options: [
                { id: 'pdf', label: 'Uploadable PDFs', value: 'pdf' },
                { id: 'cms', label: 'Editable in the CMS', value: 'cms' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'content-management',
      title: 'Content Management',
      description: 'Content and maintenance requirements',
      order: 7,
      questions: [],
      subsections: [
        {
          id: 'content-management-main',
          title: 'Content Management',
          description: 'Content and maintenance requirements',
          order: 1,
          questions: [
            {
              id: 'contentManagementNeeds',
              type: 'textarea' as const,
              title: 'Content Management Needs',
              description: 'Describe your content management requirements',
              placeholder: 'Who will update content, how often, what type of content...',
              required: false
            },
            {
              id: 'initialContentUpload',
              type: 'select' as const,
              title: 'Initial Content Upload',
              description: 'How will the initial content be provided?',
              required: false,
              options: [
                { id: 'client', label: 'Client will provide all content', value: 'client' },
                { id: 'sdco', label: 'SDCO will create content', value: 'sdco' },
                { id: 'mixed', label: 'Mixed approach', value: 'mixed' },
                { id: 'default', label: 'Use placeholder content initially', value: 'default' }
              ]
            }
          ]
        }
      ]
    }
  ],
  settings: {
    allowSaveProgress: true,
    allowBackNavigation: true,
    showProgressBar: true,
    requireAuthentication: false,
    thankYouMessage: 'Thank you for completing the questionnaire! We will review your responses and get back to you soon with a detailed proposal.',
    redirectUrl: '/questionnaire/success'
  },
  metadata: {
    category: 'website-development',
    tags: ['website', 'development', 'proposal'],
    estimatedTimeMinutes: 20
  }
};